import { useEffect, useRef } from 'react';

interface PlasmaOverlayProps {
  colors?: {
    primary?: string;
    secondary?: string;
  };
  opacity?: number;
}

export default function PlasmaOverlay({ colors, opacity = 0.3 }: PlasmaOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();

    // Parse CSS color string (hex or rgb/rgba) to [r,g,b]
    const parseCssColorToRgb = (input: string): [number, number, number] => {
      let c = input.trim();
      if (!c) return [10, 116, 255];
      const rgbMatch = c.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1].split(',').map(v => parseFloat(v.trim()));
        return [
          Math.max(0, Math.min(255, parts[0] || 0)),
          Math.max(0, Math.min(255, parts[1] || 0)),
          Math.max(0, Math.min(255, parts[2] || 0)),
        ];
      }
      if (c.startsWith('#')) c = c.slice(1);
      if (c.length === 3) c = c.split('').map(ch => ch + ch).join('');
      if (c.length !== 6) return [10, 116, 255];
      const r = parseInt(c.slice(0,2), 16);
      const g = parseInt(c.slice(2,4), 16);
      const b = parseInt(c.slice(4,6), 16);
      return [r,g,b];
    };

    const animate = () => {
      const imageData = ctx.createImageData(canvas.width, canvas.height);
      const data = imageData.data;
      time += 0.05;

      // Read header accent each frame so plasma updates to theme/accent changes
      const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-header-accent')
        .trim() || '#0A74FF';
      const [ar, ag, ab] = parseCssColorToRgb(accent);

      for (let y = 0; y < canvas.height; y += 2) {
        for (let x = 0; x < canvas.width; x += 2) {
          const index = (y * canvas.width + x) * 4;
          
          // Plasma calculation
          const value = Math.sin(x * 0.01 + time) +
                       Math.sin(y * 0.01 + time) +
                       Math.sin((x + y) * 0.01 + time) +
                       Math.sin(Math.sqrt(x * x + y * y) * 0.01 + time);
          
          const normalized = (value + 4) / 8; // 0..1
          // Modulate accent brightness by normalized factor
          const scale = 0.35 + normalized * 0.65; // keep some visibility in low regions
          const r = Math.max(0, Math.min(255, Math.floor(ar * scale)));
          const g = Math.max(0, Math.min(255, Math.floor(ag * scale)));
          const b = Math.max(0, Math.min(255, Math.floor(ab * scale)));
          const a = Math.max(40, Math.min(160, Math.floor(60 + normalized * 100))); // soft alpha window
          
          data[index] = r;
          data[index + 1] = g;
          data[index + 2] = b;
          data[index + 3] = a;
          
          // Fill 2x2 block for performance
          if (x + 1 < canvas.width) {
            const nextIndex = (y * canvas.width + (x + 1)) * 4;
            data[nextIndex] = r;
            data[nextIndex + 1] = g;
            data[nextIndex + 2] = b;
            data[nextIndex + 3] = a;
          }
          
          if (y + 1 < canvas.height) {
            const belowIndex = ((y + 1) * canvas.width + x) * 4;
            data[belowIndex] = r;
            data[belowIndex + 1] = g;
            data[belowIndex + 2] = b;
            data[belowIndex + 3] = a;
            
            if (x + 1 < canvas.width) {
              const diagIndex = ((y + 1) * canvas.width + (x + 1)) * 4;
              data[diagIndex] = r;
              data[diagIndex + 1] = g;
              data[diagIndex + 2] = b;
              data[diagIndex + 3] = a;
            }
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [colors, opacity]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: -1,
        opacity: opacity
      }}
    />
  );
}
